import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Leaf, Check, Loader2, FlaskConical, Droplets, Factory, Beaker, Heart, Feather, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = "";

const RegisterExtractor = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    owner_name: '',
    email: '',
    phone: '',
    city: '',
    description: '',
    extraction_types: '',
    products: '',
    website: '',
    instagram: '',
    facebook: '',
    image_url: '',
    years_experience: '',
    accepts_wholesale: false,
    white_label: false,
    licensed: true,
    women_owned: false,
    native_owned: false,
    bipoc_owned: false
  });

  const extractionTypes = [
    { id: 'rosin', label: 'Rosin', icon: Droplets, desc: 'Solventless extraction' },
    { id: 'bho', label: 'BHO', icon: FlaskConical, desc: 'Butane hash oil' },
    { id: 'co2', label: 'CO2', icon: Factory, desc: 'Supercritical CO2' },
    { id: 'ethanol', label: 'Ethanol', icon: Beaker, desc: 'Ethanol extraction' }
  ];

  const [selectedTypes, setSelectedTypes] = useState([]);

  const handleTypeToggle = (typeId) => {
    setSelectedTypes(prev => {
      const newTypes = prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId];
      setFormData(f => ({ ...f, extraction_types: newTypes.join(', ') }));
      return newTypes;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.owner_name || !formData.email || !formData.phone || !formData.city || !formData.description || selectedTypes.length === 0) {
      toast.error('Please fill in all required fields and select at least one extraction type');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        extraction_types: selectedTypes.join(', '),
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null
      };
      
      await axios.post(`${API}/api/extractors/register`, submitData);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background via-purple-950/10 to-background flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-md w-full"
        >
          <Card className="bg-card/80 backdrop-blur border-purple-500/20">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-purple-500" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Registration Submitted!</h2>
              <p className="text-muted-foreground mb-6">
                Thank you for registering as an extractor. Our team will review your application and get back to you within 2-3 business days.
              </p>
              <div className="space-y-2">
                <Button onClick={() => navigate('/canna-club')} className="w-full bg-purple-600 hover:bg-purple-700">
                  <Leaf className="mr-2" size={16} />
                  Back to Canna-Club
                </Button>
                <Button variant="outline" onClick={() => navigate('/')} className="w-full">
                  Go to Homepage
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-purple-950/10 to-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/canna-club')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <FlaskConical className="text-purple-500" size={20} />
              Extractor Registration
            </h1>
            <p className="text-xs text-muted-foreground">Join the Canna-Club extractor network</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          {/* Info Banner */}
          <Card className="mb-6 bg-purple-500/10 border-purple-500/20">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <FlaskConical className="text-purple-500 shrink-0 mt-1" size={20} />
                <div>
                  <h3 className="font-semibold text-purple-400">21+ Only - Minnesota Licensed</h3>
                  <p className="text-sm text-muted-foreground">
                    Registration is for licensed cannabis extraction businesses in Minnesota. All submissions are reviewed by our team.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Info */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Business Information</CardTitle>
                <CardDescription>Tell us about your extraction operation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleChange}
                      placeholder="Your extraction company name"
                      required
                      data-testid="extractor-business-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="owner_name">Owner Name *</Label>
                    <Input
                      id="owner_name"
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleChange}
                      placeholder="Full name"
                      required
                      data-testid="extractor-owner-name"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      required
                      data-testid="extractor-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                      required
                      data-testid="extractor-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City (Minnesota) *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Minneapolis"
                      required
                      data-testid="extractor-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="years_experience">Years Experience</Label>
                    <Input
                      id="years_experience"
                      name="years_experience"
                      type="number"
                      min="0"
                      value={formData.years_experience}
                      onChange={handleChange}
                      placeholder="5"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Extraction Types */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Extraction Methods *</CardTitle>
                <CardDescription>Select all extraction types you offer</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {extractionTypes.map((type) => (
                    <div
                      key={type.id}
                      onClick={() => handleTypeToggle(type.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedTypes.includes(type.id)
                          ? 'border-purple-500 bg-purple-500/10'
                          : 'border-border hover:border-purple-500/50'
                      }`}
                      data-testid={`extraction-type-${type.id}`}
                    >
                      <type.icon className={`mb-2 ${selectedTypes.includes(type.id) ? 'text-purple-500' : 'text-muted-foreground'}`} size={24} />
                      <h4 className="font-medium text-sm">{type.label}</h4>
                      <p className="text-xs text-muted-foreground">{type.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Products & Services */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Products & Services</CardTitle>
                <CardDescription>What do you produce?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="products">Products Offered</Label>
                  <Input
                    id="products"
                    name="products"
                    value={formData.products}
                    onChange={handleChange}
                    placeholder="e.g., Concentrates, Edibles, Tinctures, Vape Cartridges"
                  />
                </div>

                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.accepts_wholesale}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, accepts_wholesale: checked }))}
                    />
                    <span className="text-sm">Wholesale Available</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.white_label}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, white_label: checked }))}
                    />
                    <span className="text-sm">White Label Services</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.licensed}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, licensed: checked }))}
                    />
                    <span className="text-sm">State Licensed</span>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Ownership & Identity */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Business Ownership</CardTitle>
                <CardDescription>Help us highlight diverse businesses (optional)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-pink-500/5 hover:border-pink-500/30 transition-colors">
                    <Checkbox
                      checked={formData.women_owned}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, women_owned: checked }))}
                    />
                    <div className="flex items-center gap-2">
                      <Heart size={16} className="text-pink-400" />
                      <span className="text-sm">Women Owned</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-orange-500/5 hover:border-orange-500/30 transition-colors">
                    <Checkbox
                      checked={formData.native_owned}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, native_owned: checked }))}
                    />
                    <div className="flex items-center gap-2">
                      <Feather size={16} className="text-orange-400" />
                      <span className="text-sm">Native Owned</span>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-border cursor-pointer hover:bg-amber-500/5 hover:border-amber-500/30 transition-colors">
                    <Checkbox
                      checked={formData.bipoc_owned}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, bipoc_owned: checked }))}
                    />
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-amber-400" />
                      <span className="text-sm">BIPOC Owned</span>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">About Your Operation *</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Tell us about your extraction business, equipment, quality standards, and what makes you unique..."
                  rows={4}
                  required
                  data-testid="extractor-description"
                />
              </CardContent>
            </Card>

            {/* Social & Web */}
            <Card className="bg-card/50 backdrop-blur border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Online Presence</CardTitle>
                <CardDescription>Optional but recommended</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="instagram">Instagram</Label>
                    <Input
                      id="instagram"
                      name="instagram"
                      value={formData.instagram}
                      onChange={handleChange}
                      placeholder="@yourusername"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="facebook">Facebook</Label>
                    <Input
                      id="facebook"
                      name="facebook"
                      value={formData.facebook}
                      onChange={handleChange}
                      placeholder="facebook.com/yourpage"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image_url">Business Image URL</Label>
                  <Input
                    id="image_url"
                    name="image_url"
                    type="url"
                    value={formData.image_url}
                    onChange={handleChange}
                    placeholder="https://example.com/your-image.jpg"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-purple-600 hover:bg-purple-700 py-6 text-lg"
              data-testid="extractor-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={20} />
                  Submitting...
                </>
              ) : (
                <>
                  <FlaskConical className="mr-2" size={20} />
                  Submit Registration
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              By submitting, you confirm you are 21+ and agree to our terms of service.
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterExtractor;
